import { useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarketView from './MarketView';
import type { MarketViewProps } from './MarketView';

interface SegmentMarketViewData {
  market_kpis?: Record<string, unknown>;
  hcp_characteristics?: Record<string, unknown>;
  treatment_trends?: Record<string, unknown>;
}

interface SegmentMarketViewPanelProps {
  data: SegmentMarketViewData | undefined | null;
  segmentName?: string;
}

export default function SegmentMarketViewPanel({ data, segmentName }: SegmentMarketViewPanelProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);

  if (!data) return null;

  const hasContent = data.market_kpis || data.hcp_characteristics || data.treatment_trends;
  if (!hasContent) return null;

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <span>Segment Characteristics</span>
        <ExpandIcon $expanded={expanded}><ExpandMoreIcon /></ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          <MarketView
            marketKpis={data.market_kpis as MarketViewProps['marketKpis']}
            hcpCharacteristics={data.hcp_characteristics as MarketViewProps['hcpCharacteristics']}
            treatmentTrends={data.treatment_trends as MarketViewProps['treatmentTrends']}
          />
          <SourceBadge>AI-Generated · Segment Insights</SourceBadge>
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const SourceBadge = styled.div`
  font-size: 11px;
  color: #5C73AD;
  background-color: #f1f0f6;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  font-weight: 600;
`;
